import { fonts, fontsLineHeight, getColours, richTextHelper } from "@helpers";
import { useRouter } from "next/router";
import type { IContentCardModuleFields } from "@types-contentful";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import CTAButton from "./CTAButton";
import { useBrazeContentCards } from "@hooks/useBrazeContentCards";
import { useBrazeCanvasRegistry } from "@contexts/BrazeCanvasContext";
import { safeJsonParse } from "@utils/safeJsonParse";

type BrazeRichTextContent = {
	content?: Array<{
		content?: Array<{
			value?: string;
		}>;
	}>;
};

const Container = styled.div<{
	resetModuleMarginBottom: "No" | "Yes" | undefined;
	backgroundColour:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  background-color: ${(props) => getColours(props.backgroundColour)};
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  padding: 40px 35px;
  @media (min-width: 834px) {
    padding: 40px 10%;
  }
`;

const TitleWrapper = styled.div<{
	fontColour:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  margin: 0 auto;
  width: 304px;
  h2 {
    margin-bottom: 24px;
    color: ${(props) => props.fontColour === "Midnight Blue" && "white"};
  }
  @media (min-width: 860px) {
    width: 100%;
    max-width: 1136px;
    h2 {
      margin-bottom: 32px;
    }
  }
`;

const InnerDiv = styled.div<{
	cardCount: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  gap: 16px;

  @media screen and (min-width: 860px) {
    flex-direction: ${(props) => (props.cardCount > 1 ? "row" : "column")};
    align-items: ${(props) => (props.cardCount > 1 ? "stretch" : "flex-start")};
  }
`;
const Div = styled.div<{
	cardCount: number;
}>`
  @media screen and (max-width: 859px) {
    max-width: 304px;
  }
  overflow: auto;
  @media screen and (min-width: 860px) {
    width: ${(props) => props.cardCount === 1 && "calc(100vw - 21vw)"};
    display: flex;
    flex-direction: ${(props) => (props.cardCount > 1 ? "column" : "row")};
    min-height: ${(props) => props.cardCount === 1 && "332px"};
  }

  @media screen and (min-width: 1440px) {
    width: ${(props) => props.cardCount === 1 && "1120px"};
  }
`;

const BackgroundImage = styled.div<{
	cardCount: number;
	backgroundUrl: string;
}>`
  width: ${(props) => (props.cardCount > 1 ? "100%" : "100%")};
  min-height: 168px;
  background: url(${(props) => props.backgroundUrl}) no-repeat center center;
  background-size: cover;
  position: relative;
  border-radius: 12px 12px 0 0;
  @media screen and (min-width: 860px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: ${(props) => (props.cardCount > 1 ? "284px" : "332px")};
    border-radius: ${(props) =>
			props.cardCount > 1 ? "12px 12px 0 0" : "12px 0 0 12px"};
  }
`;

const Curve = styled.div<{
	cardCount: number;
	curveBackgroundColour:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  width: 100%;
  height: 55px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="304" height="55" viewBox="0 0 304 55"><path d="M0 38.6072C0 38.6072 79.446 62.9927 170.457 48.5857C261.468 34.1787 304 0 304 0V55H0V38.6072Z" fill="${(
		props,
	) => encodeURIComponent(getColours(props.curveBackgroundColour))}"/></svg>')
    no-repeat center bottom;
  background-size: 101%;
  position: absolute;
  bottom: -1px;

  @media screen and (min-width: 860px) {
    width: 100%;

    width: ${(props) => props.cardCount === 1 && "60px"};
    height: 100%;
    ${(props) =>
			props.cardCount === 1 &&
			`
      background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="332" viewBox="0 0 60 332"><path d="M37.8452 -10C37.8452 -10 61.7494 81.4674 47.6268 186.25C33.5041 291.033 1.5299e-05 340 1.5299e-05 340L60 340L60 -10L37.8452 -10Z" fill="${encodeURIComponent(
				getColours(props.curveBackgroundColour),
			)}"/></svg>') no-repeat center;
      background-size: cover;
      right: -1px;
      `}
    bottom: ${(props) => (props.cardCount > 1 ? "-1px" : "0")};
  }
`;

const Content = styled.div<{
	cardCount: number;
	contentBackgroundColour:
		| "Midnight Blue"
		| "White"
		| "Peach"
		| "Pale Peach"
		| "Watermelon"
		| "Mint Green"
		| "Green"
		| undefined;
}>`
  background-color: ${(props) => getColours(props.contentBackgroundColour)};
  padding: 20px;
  height: 50%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: ${(props) => props.cardCount > 1 && "1"};
  font-size: 20px;
  box-sizing: border-box;
  border-radius: 0 0 12px 12px;
  a {
    margin-top: 32px;
  }
  @media screen and (min-width: 860px) {
    padding: ${(props) =>
			props.cardCount > 1 ? "40px" : "70px 40px 40px 40px"};
    height: ${(props) => props.cardCount === 1 && "100%"};
    min-height: ${(props) => props.cardCount === 1 && "332px"};
    max-width: ${(props) => props.cardCount > 1 && "560px"};
    align-items: flex-start;

    border-radius: ${(props) =>
			props.cardCount > 1 ? "0 0 12px 12px" : "0 12px 12px 0"};
  }
`;

const Title = styled.h5`
	font-size: ${fonts.H5};
	line-height: ${fontsLineHeight.H5};
`;

const DescriptionContent = styled.div`
	strong {
		font-weight: bold;
	}
`;

type ModuleColour =
	| "Midnight Blue"
	| "White"
	| "Peach"
	| "Pale Peach"
	| "Watermelon"
	| "Mint Green"
	| "Green"
	| undefined;

const TIMELINE_PATH = "/about-us/tree-planting/tree-projects-new";

// How far each non-first card is pulled up to overlap the previous one.
const TIMELINE_OVERLAP_PX = 120;

// No vertical margin/padding so consecutive timeline modules join into one line
const TimelineSection = styled.div<{
	backgroundColour: ModuleColour;
}>`
  margin-bottom: 0 !important;
  background-color: ${(props) => getColours(props.backgroundColour)};
  box-sizing: border-box;
  padding: 0 35px;

  @media (min-width: 834px) {
    padding: 0 10%;
  }
`;

const TimelineRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  padding: 24px 0;

  @media screen and (min-width: 860px) {
    display: grid;
    grid-template-columns: 1fr 80px 1fr;
    padding: 18px 0;
    max-width: 1136px;
    margin: 0 auto;
  }
`;

// One full wave period spanning the row, so the line is continuous row to row.
const TimelineWave = styled.div`
  display: none;

  @media screen and (min-width: 860px) {
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 80px;
    transform: translateX(-50%);
    pointer-events: none;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="200" viewBox="0 0 80 200" preserveAspectRatio="none"><path d="M40 0 C 78 42, 78 58, 40 100 C 2 142, 2 158, 40 200" fill="none" stroke="${encodeURIComponent(
			getColours("Midnight Blue"),
		)}" stroke-width="1.5" vector-effect="non-scaling-stroke"/></svg>')
      no-repeat center;
    background-size: 100% 100%;
  }
`;

// Sits where the wave crosses the centre line (fixed at the row's own
// midpoint - the card's overlap doesn't move the wave, so neither should this).
const TimelineDot = styled.span<{ dotBackgroundColour: ModuleColour }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  border: 1.5px solid ${getColours("Midnight Blue")};
  border-radius: 50%;
  background-color: ${(props) => getColours(props.dotBackgroundColour)};
`;

const TimelineCard = styled.div<{ side: "left" | "right"; overlap: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 304px;

  @media screen and (min-width: 860px) {
    grid-row: 1;
    grid-column: ${(props) => (props.side === "left" ? 1 : 3)};
    justify-self: ${(props) => (props.side === "left" ? "end" : "start")};
    align-items: ${(props) => (props.side === "left" ? "flex-end" : "stretch")};
    text-align: ${(props) => (props.side === "left" ? "right" : "left")};
    max-width: 420px;
    margin-top: ${(props) => (props.overlap ? `-${TIMELINE_OVERLAP_PX}px` : "0")};
  }
`;

// Dedicated to the timeline layout so it doesn't affect the shared BackgroundImage.
const TimelineImage = styled.div<{ backgroundUrl: string }>`
  position: relative;
  width: 70%;
  min-height: 184px;
  background: url(${(props) => props.backgroundUrl}) no-repeat center center;
  background-size: cover;
  border-radius: 12px 12px 0 0;
`;

const TimelineDate = styled.h2<{ fontColour: ModuleColour }>`
  margin: 24px 0 16px 0;
  color: ${(props) =>
		props.fontColour === "Midnight Blue" ? getColours("White") : "inherit"};
`;

const TimelineBody = styled.div`
  font-size: 14px;
  line-height: 20px;

  strong {
    font-weight: bold;
  }

  p {
    margin: 0 0 12px 0;
  }
`;

// Helper function to parse Braze content which might be HTML or JSON
const parseBrazeContent = (content: string | undefined): string => {
	if (!content) return "";

	const parsed = safeJsonParse<BrazeRichTextContent>(
		content,
		{},
		{ component: "ContentCardModule", action: "parse_braze_content" },
	);

	if (!parsed.success) {
		return content;
	}

	if (parsed.value.content?.[0]?.content) {
		return parsed.value.content[0].content
			.map((item) => item.value || "")
			.join("");
	}

	return content;
};

type BackgroundColour =
	| "Midnight Blue"
	| "White"
	| "Peach"
	| "Pale Peach"
	| "Watermelon"
	| "Mint Green"
	| "Green";

// Helper to get colour mapping from Braze extras
const getBackgroundColourFromBraze = (extras?: {
	[key: string]: string;
}): BackgroundColour => {
	const colourMap: { [key: string]: BackgroundColour } = {
		midnight_blue: "Midnight Blue",
		white: "White",
		peach: "Peach",
		pale_peach: "Pale Peach",
		watermelon: "Watermelon",
		mint_green: "Mint Green",
		green: "Green",
	};

	const bgColour = extras?.background_colour || "";
	return colourMap[bgColour] || "White";
};

const ContentfulContentCardModule = ({
	removeNegativeSpacingBelowModule,
	backgroundColour,
	header,
	contentCards,
}: IContentCardModuleFields) => {
	const { loading, getCardsByType, logCardClick, logCardImpression } =
		useBrazeContentCards();
	const router = useRouter();
	const currentPath = router?.asPath || "";
	const { shouldClaimCanvas } = useBrazeCanvasRegistry();
	// Stable id for this module instance (for diagnostics)
	const instanceIdRef = useRef<string>("");
	if (!instanceIdRef.current)
		instanceIdRef.current = Math.random().toString(36).slice(2);
	// Track which cards have logged impressions
	const loggedImpressionCardIds = useRef<Set<string>>(new Set());

	const isTimelinePage =
		currentPath.split(/[?#]/)[0].replace(/\/$/, "") === TIMELINE_PATH;
	const timelineSectionRef = useRef<HTMLDivElement | null>(null);
	// Each timeline event is its own module, so the side alternates by DOM order.
	const [timelineIndex, setTimelineIndex] = React.useState(0);

	useEffect(() => {
		if (!isTimelinePage || !timelineSectionRef.current) return;
		const sections = Array.from(
			document.querySelectorAll("[data-timeline-module]"),
		);
		const position = sections.indexOf(timelineSectionRef.current);
		if (position > -1) setTimelineIndex(position);
	}, [isTimelinePage]);

	// Fetch content_card_module cards from Braze
	const brazeContentCards = getCardsByType("content_card_module");

	// Remove duplicates and get only one card per position
	// Also filter by page_names to match the current page
	const uniqueCards = React.useMemo(() => {
		// First, filter by page_names
		const matchingCards = brazeContentCards.filter((card) => {
			const pageNames = card.extras?.page_names;
			if (!pageNames) return false;

			const pages = pageNames.split(",").map((p) => p.trim());

			return pages.some((page) => {
				if (page === currentPath) return true;
				if (page === "/*") return true;
				return false;
			});
		});

		// Create a Map to store one card per position
		const positionMap = new Map();

		// Group cards by position and keep the first one for each position
		for (const card of matchingCards) {
			const position = card.extras?.position || "unknown";
			if (!positionMap.has(position)) {
				positionMap.set(position, card);
			}
		}

		// Convert back to array and sort by position
		const sorted = Array.from(positionMap.values()).sort((a, b) => {
			const posA = a.extras?.position || "";
			const posB = b.extras?.position || "";
			return posA.localeCompare(posB);
		});

		return sorted;
	}, [brazeContentCards, currentPath]);

	// Determine canvas groups from cards (e.g., canvas_1). Treat truthy strings as one group.
	const normalizeCanvasKey = (raw: string): string => {
		const k = (raw || "")
			.toString()
			.trim()
			.toLowerCase()
			.replace(/cavase/g, "canvas"); // fix common typo
		if (!k || k === "true") return "canvas_default";
		return k;
	};

	const canvasGroups = React.useMemo(() => {
		const set = new Set<string>();
		for (const card of uniqueCards) {
			const raw = (card.extras?.braze_canvas || "") as string;
			const norm = normalizeCanvasKey(raw);
			set.add(norm);
		}
		return Array.from(set);
	}, [uniqueCards]);

	let allowBrazeForThisModule = !loading && uniqueCards.length > 0;
	if (allowBrazeForThisModule && canvasGroups.length === 1 && currentPath) {
		// Synchronously claim via a window-scoped registry so only the first module uses this canvas
		const canvasKey = canvasGroups[0];
		try {
			const w: any = window;
			// claims map keeps a Set for historical claims (legacy) and an owners map for strict-mode stability
			w.__brazeCanvasClaimsMap = w.__brazeCanvasClaimsMap || new Map();
			w.__brazeCanvasOwnersMap = w.__brazeCanvasOwnersMap || new Map();
			const map: Map<string, Set<string>> = w.__brazeCanvasClaimsMap;
			const owners: Map<string, Map<string, string>> = w.__brazeCanvasOwnersMap;
			const set = map.get(currentPath) || new Set<string>();
			const ownersForPage =
				owners.get(currentPath) || new Map<string, string>();
			const currentOwner = ownersForPage.get(canvasKey);
			const already = set.has(canvasKey);
			if (currentOwner && currentOwner !== instanceIdRef.current) {
				allowBrazeForThisModule = false; // claimed by another module instance
			} else if (!currentOwner) {
				// first claim
				set.add(canvasKey);
				map.set(currentPath, set);
				ownersForPage.set(canvasKey, instanceIdRef.current);
				owners.set(currentPath, ownersForPage);
			} else {
				// owner is this module; allow
				allowBrazeForThisModule = true;
			}
		} catch {
			// fallback to allowing if something unexpected happens - keep current value
		}
	}

	// Determine which cards to use: Braze if available, otherwise Contentful
	const useBrazeCards = allowBrazeForThisModule;
	const cardsToRender = useBrazeCards ? uniqueCards : contentCards || [];
	const cardCount = cardsToRender.length || 1;

	// Log impressions when Braze cards are rendered (once per card)
	useEffect(() => {
		if (useBrazeCards && uniqueCards.length > 0) {
			uniqueCards.forEach((card) => {
				if (card.id && !loggedImpressionCardIds.current.has(card.id)) {
					logCardImpression(card.id);
					loggedImpressionCardIds.current.add(card.id);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useBrazeCards, uniqueCards]); // Log impressions when cards change

	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	// loading resolves before the first paint (via useLayoutEffect in the hook),
	// so this effectively decides Braze vs Contentful without any visible blur or flash.
	const showBlurredPlaceholder = loading;

	// Timeline layout: alternating cards either side of a continuous wavy line
	if (!useBrazeCards && isTimelinePage) {
		return (
			<TimelineSection
				ref={timelineSectionRef}
				data-timeline-module=""
				backgroundColour={backgroundColour}
			>
				{contentCards?.map((card, index) => {
					const side = (timelineIndex + index) % 2 === 0 ? "left" : "right";
					const isFirstOverall = timelineIndex === 0 && index === 0;
					return (
						<TimelineRow key={index}>
							<TimelineWave aria-hidden="true">
								<TimelineDot dotBackgroundColour={backgroundColour} />
							</TimelineWave>
							<TimelineCard side={side} overlap={!isFirstOverall}>
								<TimelineImage
									backgroundUrl={card.fields?.image?.fields?.file?.url}
								>
									<Curve
										curveBackgroundColour={backgroundColour}
										cardCount={2}
									/>
								</TimelineImage>
								{header && (
									<TimelineDate fontColour={backgroundColour}>
										{header}
									</TimelineDate>
								)}
								<TimelineBody>
									{card.fields?.header && <Title>{card.fields.header}</Title>}
									{richTextHelper(card.fields?.subCopy)}
								</TimelineBody>
								{card?.fields?.ctaButtonUrl &&
									card?.fields?.ctaButtonColour &&
									card?.fields?.name &&
									card?.fields?.ctaButtonLabel && (
										<CTAButton
											url={card?.fields?.ctaButtonUrl}
											ctaButtonColour={card?.fields?.ctaButtonColour}
											gtmCtaPosition={card?.fields?.name}
											gtmCtaText={card?.fields?.ctaButtonLabel}
										>
											{card?.fields?.ctaButtonLabel}
										</CTAButton>
									)}
							</TimelineCard>
						</TimelineRow>
					);
				})}
			</TimelineSection>
		);
	}

	// Render Braze cards
	if (useBrazeCards) {
		return (
			<Container
				resetModuleMarginBottom={resetModuleMarginBottom}
				backgroundColour={backgroundColour}
				style={{
					// Reserve space while loading to prevent layout shift
					minHeight: "400px",
					filter: showBlurredPlaceholder ? "blur(20px)" : "none",
				}}
			>
				{header && (
					<TitleWrapper fontColour={backgroundColour}>
						<h2>{header}</h2>
					</TitleWrapper>
				)}
				<InnerDiv cardCount={cardCount}>
					{cardsToRender.map((card, index) => {
						// Match to Contentful cards for styling - use the first Contentful card's styling
						const contentfulCard = contentCards?.[index];
						const cardBackgroundColour =
							contentfulCard?.fields?.backgroundColour || backgroundColour;
						// Braze Action "Link" is in card.url (same as banners)
						const cardUrl = card.url;

						return (
							<React.Fragment key={card.id || Math.random().toString()}>
								<Div cardCount={cardCount}>
									<BackgroundImage
										backgroundUrl={card.imageUrl || ""}
										cardCount={cardCount}
									>
										<Curve
											curveBackgroundColour={cardBackgroundColour}
											cardCount={cardCount}
										/>
									</BackgroundImage>
									<Content
										contentBackgroundColour={cardBackgroundColour}
										cardCount={cardCount}
									>
										<Title style={{ marginBottom: "24px" }}>
											{parseBrazeContent(card.title)}
										</Title>
										{card.description && (
											<DescriptionContent
												dangerouslySetInnerHTML={{
													__html: parseBrazeContent(card.description),
												}}
											/>
										)}
										{cardUrl && contentfulCard?.fields?.ctaButtonLabel && (
											<CTAButton
												url={cardUrl}
												ctaButtonColour={
													contentfulCard?.fields?.ctaButtonColour ||
													"Midnight Blue"
												}
												gtmCtaPosition={
													contentfulCard?.fields?.name || "plan_page_tile"
												}
												gtmCtaText={contentfulCard?.fields?.ctaButtonLabel}
												preserveAbsoluteUrl={true}
												onClick={() => {
													if (card.id) {
														logCardClick(card.id);
													}
												}}
											>
												{contentfulCard?.fields?.ctaButtonLabel}
											</CTAButton>
										)}
									</Content>
								</Div>
							</React.Fragment>
						);
					})}
				</InnerDiv>
			</Container>
		);
	}

	// Render Contentful cards (default)
	// Show blurred while loading, then clear after loading
	return (
		<Container
			resetModuleMarginBottom={resetModuleMarginBottom}
			backgroundColour={backgroundColour}
			style={{
				// Reserve space while loading to prevent layout shift
				minHeight: "400px",
				filter: showBlurredPlaceholder ? "blur(20px)" : "none",
			}}
		>
			{header && (
				<TitleWrapper fontColour={backgroundColour}>
					<h2>{header}</h2>
				</TitleWrapper>
			)}
			<InnerDiv cardCount={cardCount}>
				{contentCards?.map((card, index) => (
					<React.Fragment key={index}>
						<Div cardCount={cardCount}>
							<BackgroundImage
								backgroundUrl={card.fields?.image?.fields?.file?.url}
								cardCount={cardCount}
							>
								<Curve
									curveBackgroundColour={card.fields?.backgroundColour}
									cardCount={cardCount}
								/>
							</BackgroundImage>
							<Content
								contentBackgroundColour={card.fields?.backgroundColour}
								cardCount={cardCount}
								style={{alignItems: "unset"}}
							>
								<Title style={{ marginBottom: "24px" }}>
									{card.fields?.header}
								</Title>
								{richTextHelper(card.fields?.subCopy)}
								{card?.fields?.ctaButtonUrl &&
									card?.fields?.ctaButtonColour &&
									card?.fields?.name &&
									card?.fields?.ctaButtonLabel && (
										<CTAButton
											url={card?.fields?.ctaButtonUrl}
											ctaButtonColour={card?.fields?.ctaButtonColour}
											gtmCtaPosition={card?.fields?.name}
											gtmCtaText={card?.fields?.ctaButtonLabel}
										>
											{card?.fields?.ctaButtonLabel}
										</CTAButton>
									)}
							</Content>
						</Div>
					</React.Fragment>
				))}
			</InnerDiv>
		</Container>
	);
};

export default ContentfulContentCardModule;
